/**
 * Die Hersteller-Einstellungen: alles, was für eine Marke gilt, an EINER Stelle.
 *
 * 🔴 Matze, 21.09.2026: „Irgendwie fehlt mir das aber noch übergeordnet, dass man eine
 * Auswahl für die Hersteller für Preise und sonstige artikelspezifische Einstellungen
 * vornehmen kann." Vorher lagen die Werte im Code-Profil, im JSONB der Profil-Tabelle
 * und in den Preis-Sets — ändern ließen sie sich nur in TypeScript.
 *
 * 🔑 **Jeder Block sagt, woher sein Stand kommt.** „aus der Messung" heißt: so sieht
 * der Bestand aus, aber niemand hat es entschieden. „gespeichert" heißt: jemand hat.
 * Ohne diesen Unterschied ändert man Werte, ohne zu wissen, ob man gerade eine
 * Entscheidung überschreibt oder eine trifft.
 *
 * Was hier gespeichert wird, schreibt NICHTS nach Plenty — es gilt ab dem nächsten
 * Lauf. Laufende Läufe bleiben unberührt.
 */
import { useCallback, useEffect, useState } from 'react';
import { Check, RefreshCw, Save } from 'lucide-react';
import { api } from '@libero/core/services/api';
import LaufButton from '../components/LaufButton';
import { zahl } from '../lib/format';
import { MARKT_NAMEN, mandantName } from '../lib/kanaele';
import type { HerstellerAnsicht, HerstellerAntwort } from '../lib/typen';

export default function HerstellerPage() {
  const [daten, setDaten] = useState<HerstellerAntwort | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gespeichertAm, setGespeichertAm] = useState<string | null>(null);
  /** Die Änderungen, die noch nicht gespeichert sind. */
  const [entwurf, setEntwurf] = useState<HerstellerAnsicht | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const antwort = await api.get<{ data: HerstellerAntwort }>(
        '/programmes/artikelimport/hersteller',
      );
      setDaten(antwort.data.data);
      const erster = antwort.data.data.hersteller[0]?.schluessel ?? null;
      setGewaehlt(vorher => vorher ?? erster);
    } catch {
      setFehler('Die Hersteller-Einstellungen ließen sich nicht laden.');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { void laden(); }, [laden]);

  // Beim Wechsel der Marke den Bearbeitungsstand mitziehen.
  useEffect(() => {
    const treffer = daten?.hersteller.find(h => h.schluessel === gewaehlt) ?? null;
    setEntwurf(treffer ? JSON.parse(JSON.stringify(treffer)) : null);
    setGespeichertAm(null);
  }, [daten, gewaehlt]);

  async function speichern() {
    if (!entwurf) return;
    setSpeichert(true);
    setFehler(null);
    try {
      await api.put(`/programmes/artikelimport/hersteller/${entwurf.schluessel}`, {
        kanaele: { mandanten: entwurf.kanaele.mandanten, maerkte: entwurf.kanaele.maerkte },
        sichtbarkeit: entwurf.sichtbarkeit.werte,
        amazon: entwurf.amazon.werte,
        eigenschaften: { vorgaben: entwurf.eigenschaften.vorgaben, tags: entwurf.eigenschaften.tags },
        texte: {
          aktiv: entwurf.texte.aktiv,
          kiProsa: entwurf.texte.kiProsa,
          versandSchwelleEuro: entwurf.texte.versandSchwelleEuro,
        },
        stammwerte: entwurf.stammwerte,
      });
      setGespeichertAm(new Date().toLocaleTimeString('de-DE'));
      await laden();
    } catch {
      setFehler('Das Speichern ist fehlgeschlagen. Die Werte stehen noch wie vorher.');
    } finally {
      setSpeichert(false);
    }
  }

  if (fehler && !daten) {
    return <p className="p-6 text-sm text-red-700">{fehler}</p>;
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-[#1A1A1A]">Hersteller-Einstellungen</h2>
          <p className="text-sm text-[#1A1A1A]/60">
            Was hier steht, gilt für jeden neuen Artikel dieser Marke. Es wird nichts nach
            PlentyMarkets geschrieben — die Werte wirken ab dem nächsten Lauf.
          </p>
        </div>
        <LaufButton
          laeuft={laedt}
          laufText="Einstellungen werden geladen"
          icon={<RefreshCw className="h-4 w-4" />}
          onClick={() => void laden()}
          variant="outline"
          size="sm"
        >
          Neu laden
        </LaufButton>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-[#DAD9D9]">
        {(daten?.hersteller ?? []).map(h => (
          <button
            key={h.schluessel}
            type="button"
            onClick={() => setGewaehlt(h.schluessel)}
            className={`rounded-t border-b-2 px-4 py-2 text-sm ${
              gewaehlt === h.schluessel
                ? 'border-[#1A1A1A] font-medium text-[#1A1A1A]'
                : 'border-transparent text-[#1A1A1A]/60 hover:text-[#1A1A1A]'
            }`}
          >
            {h.hersteller}
            {!h.aktiv && <span className="ml-2 text-xs text-[#1A1A1A]/40">(inaktiv)</span>}
          </button>
        ))}
      </div>

      {entwurf && (
        <div className="mt-4 space-y-3">
          {fehler && <p className="text-sm text-red-700">{fehler}</p>}

          <Block titel="Preise" herkunft={null}>
            <p className="text-sm text-[#1A1A1A]/70">
              Preis-Set <strong>{entwurf.preise.setName ?? 'keins'}</strong>
              {entwurf.preise.rabattDefault != null && (
                <> · Kondition {entwurf.preise.rabattDefault} %</>
              )}
              {entwurf.preise.rabatte > 0 && (
                <> · {zahl(entwurf.preise.rabatte)} Rabattzeilen nach Kategorie</>
              )}
            </p>
            <table className="mt-2 text-sm">
              <tbody>
                {entwurf.preise.zeilen.map(z => (
                  <tr key={z.preisart} className={z.aktiv ? '' : 'text-[#1A1A1A]/40'}>
                    <td className="py-0.5 pr-4 font-medium">{z.preisart}</td>
                    <td className="py-0.5 pr-4 font-mono text-xs">{z.ausdruck}</td>
                    <td className="py-0.5 text-xs">{z.aktiv ? '' : 'inaktiv'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-[#1A1A1A]/50">
              Die Formeln stehen in den Preis-Sets und werden dort gepflegt — hier nur zur
              Ansicht, damit man sie im Zusammenhang sieht.
            </p>
          </Block>

          <Block titel="Kanäle" herkunft={entwurf.kanaele.herkunft}>
            <Feldzeile label="Mandant">
              {entwurf.kanaele.mandanten.length === 0
                ? <span className="text-amber-800">keiner — der Artikel wäre nirgends sichtbar</span>
                : entwurf.kanaele.mandanten.map(mandantName).join(', ')}
            </Feldzeile>
            <p className="mt-2 text-xs font-medium text-[#1A1A1A]/70">
              Märkte ({zahl(entwurf.kanaele.maerkte.length)})
            </p>
            <div className="mt-1 flex flex-wrap gap-1">
              {Object.entries(MARKT_NAMEN).map(([id, name]) => {
                const zahlId = Number(id);
                const an = entwurf.kanaele.maerkte.includes(zahlId);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setEntwurf(e => e && ({
                      ...e,
                      kanaele: {
                        ...e.kanaele,
                        maerkte: an
                          ? e.kanaele.maerkte.filter(m => m !== zahlId)
                          : [...e.kanaele.maerkte, zahlId],
                      },
                    }))}
                    className={`rounded border px-2 py-1 text-xs ${
                      an
                        ? 'border-[#1A1A1A] bg-[#E9FF57] text-[#1A1A1A]'
                        : 'border-[#DAD9D9] text-[#1A1A1A]/60 hover:border-[#1A1A1A]/40'
                    }`}
                  >
                    {an && <Check className="mr-1 inline h-3 w-3" />}
                    {name}
                  </button>
                );
              })}
            </div>
          </Block>

          <Block titel="Verfügbarkeit und Sichtbarkeit" herkunft={entwurf.sichtbarkeit.herkunft}>
            {SICHTBARKEIT_LABEL.map(([schluessel, label, erklaerung]) => (
              <label key={schluessel} className="flex items-start gap-2 py-1 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={entwurf.sichtbarkeit.werte[schluessel] === true}
                  onChange={e => setEntwurf(v => v && ({
                    ...v,
                    sichtbarkeit: {
                      ...v.sichtbarkeit,
                      werte: { ...v.sichtbarkeit.werte, [schluessel]: e.target.checked },
                    },
                  }))}
                />
                <span>
                  {label}
                  <span className="ml-2 text-xs text-[#1A1A1A]/50">{erklaerung}</span>
                </span>
              </label>
            ))}
            <Feldzeile label="Bestandsbeschränkung">
              {entwurf.sichtbarkeit.werte.stockLimitation === 1
                ? 'nur auf Netto-Warenbestand'
                : 'unbeschränkt'}
            </Feldzeile>
          </Block>

          <Block titel="Stammwerte" herkunft={null}>
            <Feldzeile label="Hersteller-Id">{entwurf.plentyProducerId ?? '–'}</Feldzeile>
            <Feldzeile label="Lager">{entwurf.stammwerte.lagerId ?? '–'}</Feldzeile>
            <Feldzeile label="Lieferzeit-Anzeige">
              {entwurf.stammwerte.verfuegbarkeitId ?? '–'}
              <span className="ml-2 text-xs text-[#1A1A1A]/50">
                Startwert — Plenty rechnet ihn danach selbst aus dem Bestand.
              </span>
            </Feldzeile>
            <Feldzeile label="Steuersatz">{entwurf.stammwerte.steuersatz ?? '–'}</Feldzeile>
            <Feldzeile label="Versandprofile">
              {entwurf.stammwerte.versandprofile.join(', ') || '–'}
            </Feldzeile>
          </Block>

          <Block titel="Varianteneigenschaften" herkunft={entwurf.eigenschaften.herkunft}>
            {entwurf.eigenschaften.vorgaben.length === 0
              ? <p className="text-sm text-[#1A1A1A]/60">Keine — die Varianten bleiben ohne.</p>
              : (
                <ul className="space-y-1 text-sm">
                  {entwurf.eigenschaften.vorgaben.map(v => (
                    <li key={v.propertyId} className="flex items-baseline gap-2">
                      <span>{v.bezeichnung}</span>
                      <span className="text-xs text-[#1A1A1A]/40">
                        Eigenschaft {v.propertyId}
                        {v.auswahlId != null && ` · Wert ${v.auswahlId}`}
                      </span>
                      {v.nurTextilien && (
                        <span className="rounded bg-[#F7F7F5] px-2 py-0.5 text-xs text-[#1A1A1A]/70">
                          nur bei Textilien
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            <Feldzeile label="Tags">
              {entwurf.eigenschaften.tags.length === 0
                ? '–'
                : entwurf.eigenschaften.tags.map(t => (
                  <span key={t.tagId} className="mr-2 inline-block">
                    {t.bezeichnung} ({t.tagId})
                    {t.nurTextilien && (
                      <span className="ml-1 rounded bg-[#F7F7F5] px-1 text-xs text-[#1A1A1A]/70">
                        nur Textilien
                      </span>
                    )}
                  </span>
                ))}
            </Feldzeile>
            <p className="mt-2 text-xs text-[#1A1A1A]/50">
              „Nur bei Textilien" heißt: die Eigenschaft wird gesetzt, wenn die Variante eine
              Größe trägt. Bälle und Zubehör bekommen sie nicht.
            </p>
          </Block>

          <Block titel="Amazon" herkunft={entwurf.amazon.herkunft}>
            <Feldzeile label="Produkttyp Bekleidung">
              {String(entwurf.amazon.werte.produkttypBekleidung ?? '–')}
              <span className="ml-2 text-xs text-[#1A1A1A]/50">248 = Sports / SportingGoods</span>
            </Feldzeile>
            <Feldzeile label="Produkttyp sonstiges">
              {String(entwurf.amazon.werte.produkttypSonstiges ?? '–')}
              <span className="ml-2 text-xs text-[#1A1A1A]/50">278 = Offer (Bälle, Zubehör)</span>
            </Feldzeile>
            <Feldzeile label="Produkttyp (neu)">
              {String(entwurf.amazon.werte.produkttyp2 ?? '–')}
            </Feldzeile>
            <Feldzeile label="FBA">
              {entwurf.amazon.werte.fbaPlattform === 0 ? 'nicht verwenden' : String(entwurf.amazon.werte.fbaPlattform)}
              {' · '}
              Versand durch Amazon: {entwurf.amazon.werte.versandDurchAmazon ? 'ja' : 'nein'}
            </Feldzeile>
          </Block>

          <Block titel="Texte" herkunft={entwurf.texte.herkunft}>
            <label className="flex items-center gap-2 py-1 text-sm">
              <input
                type="checkbox"
                checked={entwurf.texte.aktiv}
                onChange={e => setEntwurf(v => v && ({
                  ...v, texte: { ...v.texte, aktiv: e.target.checked },
                }))}
              />
              Texte beim Anlegen schreiben
            </label>
            <label className="flex items-center gap-2 py-1 text-sm">
              <input
                type="checkbox"
                checked={entwurf.texte.kiProsa}
                disabled={!entwurf.texte.aktiv}
                onChange={e => setEntwurf(v => v && ({
                  ...v, texte: { ...v.texte, kiProsa: e.target.checked },
                }))}
              />
              Einleitung, Meta-Beschreibung und Keywords von Gemini schreiben lassen
              <span className="text-xs text-[#1A1A1A]/50">
                (ohne Haken: nur die Regel-Fassung)
              </span>
            </label>
            <label className="flex items-center gap-2 py-1 text-sm">
              Versandkostenfrei ab
              <input
                type="number"
                min={0}
                value={entwurf.texte.versandSchwelleEuro}
                onChange={e => setEntwurf(v => v && ({
                  ...v,
                  texte: { ...v.texte, versandSchwelleEuro: Number(e.target.value) },
                }))}
                className="w-20 rounded border border-[#DAD9D9] px-2 py-1 text-sm"
              />
              € — steht so in der Meta-Beschreibung
            </label>
            <p className="mt-2 text-xs text-[#1A1A1A]/50">
              Technische Daten und die Merkmalsliste entstehen immer aus der Herstellerdatei,
              auch ohne Gemini. Fällt das Modell aus, bleibt der Regeltext stehen — ein
              Artikel scheitert nie an einem Textfehler.
            </p>
          </Block>

          <Block titel="Quelle und Bilder" herkunft={null}>
            <Feldzeile label="Quelle">{entwurf.quelle.typ ?? '–'}</Feldzeile>
            <Feldzeile label="Blatt">{entwurf.quelle.blatt ?? '–'}</Feldzeile>
            <Feldzeile label="Bildordner">{entwurf.quelle.bildOrdner ?? 'keiner vorbelegt'}</Feldzeile>
            <Feldzeile label="Mehrere Dateien">
              {entwurf.quelle.mehrereDateien ? 'ja' : 'nein'}
            </Feldzeile>
          </Block>

          <div className="flex items-center gap-3 pt-2">
            <LaufButton
              laeuft={speichert}
              laufText="Einstellungen werden gespeichert"
              icon={<Save className="h-4 w-4" />}
              onClick={() => void speichern()}
            >
              Speichern
            </LaufButton>
            {gespeichertAm && (
              <span className="text-sm text-green-800">Gespeichert um {gespeichertAm}.</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Die Schalter aus dem Plenty-Reiter „Verfügbarkeit", mit der Erklärung dahinter.
 *
 * Die Reihenfolge ist die der Plenty-Oberfläche — wer beides nebeneinander hat, soll
 * nicht suchen müssen.
 */
const SICHTBARKEIT_LABEL: Array<[string, string, string]> = [
  ['isAvailableIfNetStockIsPositive', 'Automatisch verfügbar, wenn Netto-WB positiv', 'bestellbar, sobald Bestand da ist'],
  ['isUnavailableIfNetStockIsNotPositive', 'Automatisch nicht verfügbar, wenn kein Netto-WB', 'nicht bestellbar bei leerem Bestand'],
  ['isVisibleIfNetStockIsPositive', 'Mandant: automatisch sichtbar, wenn Netto-WB positiv', 'erscheint im Shop, sobald Bestand da ist'],
  ['isInvisibleIfNetStockIsNotPositive', 'Mandant: automatisch unsichtbar, wenn kein Netto-WB', 'verschwindet bei leerem Bestand ganz'],
  ['isVisibleInListIfNetStockIsPositive', 'Artikelauflistung: automatisch sichtbar', 'erscheint in Kategorielisten'],
  ['isInvisibleInListIfNetStockIsNotPositive', 'Artikelauflistung: automatisch unsichtbar', 'verschwindet aus Listen bei leerem Bestand'],
  ['isHiddenInCategoryList', 'In Kategorieliste ausblenden', 'dauerhaft aus Listen nehmen'],
];

function Block(
  { titel, herkunft, children }:
  { titel: string; herkunft: 'profil' | 'messung' | 'code' | null; children: React.ReactNode },
) {
  return (
    <section className="rounded border border-[#DAD9D9] bg-white p-3">
      <div className="mb-2 flex items-baseline gap-3">
        <h3 className="text-sm font-medium text-[#1A1A1A]">{titel}</h3>
        {herkunft && <HerkunftSchild herkunft={herkunft} />}
      </div>
      {children}
    </section>
  );
}

/**
 * Woher der Stand kommt.
 *
 * „aus der Messung" ist kein Makel, sondern eine Warnung: der Wert bildet den Bestand
 * ab, aber niemand hat ihn entschieden.
 */
function HerkunftSchild({ herkunft }: { herkunft: 'profil' | 'messung' | 'code' }) {
  if (herkunft === 'profil') {
    return (
      <span className="rounded bg-green-50 px-2 py-0.5 text-xs text-green-900">
        gespeichert
      </span>
    );
  }
  if (herkunft === 'code') {
    return (
      <span className="rounded bg-[#F7F7F5] px-2 py-0.5 text-xs text-[#1A1A1A]/70">
        fest im Programm
      </span>
    );
  }
  return (
    <span
      className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-900"
      title="So sieht der Bestand aus (Vermessung 21.09.2026) — entschieden hat es noch niemand."
    >
      aus der Messung
    </span>
  );
}

function Feldzeile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="py-0.5 text-sm">
      <span className="text-[#1A1A1A]/50">{label}: </span>
      <span className="text-[#1A1A1A]">{children}</span>
    </div>
  );
}
